import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Send, Bell, CheckCheck, Trash2 } from "lucide-react";

const AUDIENCES = [
  { v: "all", l: "Everyone" },
  { v: "students", l: "Students only" },
  { v: "teachers", l: "Teachers only" },
];
const NONE = "__none__";
const emptyForm = { title: "", body: "", audience: "all", branch_id: NONE, course_id: NONE, class_id: NONE, user_ids: [] };

/* ---------------- Manager: send & track notifications ---------------- */
export function NotificationsAdminTab() {
  const [form, setForm] = useState(emptyForm);
  const [branches, setBranches] = useState([]);
  const [courses, setCourses] = useState([]);
  const [classes, setClasses] = useState([]);
  const [users, setUsers] = useState([]);
  const [sent, setSent] = useState([]);
  const [preview, setPreview] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/notifications/sent").then((r) => setSent(r.data)).catch(() => setSent([]));
  useEffect(() => {
    load();
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => setBranches([]));
    api.get("/courses").then((r) => setCourses(r.data)).catch(() => setCourses([]));
    api.get("/classes").then((r) => setClasses(r.data)).catch(() => setClasses([]));
    api.get("/users").then((r) => setUsers(r.data)).catch(() => setUsers([]));
  }, []);

  const payload = () => ({
    title: form.title, body: form.body, audience: form.audience,
    branch_id: form.branch_id === NONE ? null : form.branch_id,
    course_id: form.course_id === NONE ? null : form.course_id,
    class_id: form.class_id === NONE ? null : form.class_id,
    user_ids: form.user_ids,
  });

  async function checkRecipients() {
    try { const r = await api.post("/notifications/preview", payload()); setPreview(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function send() {
    if (!form.title) { toast.error("Title is required"); return; }
    try {
      const r = await api.post("/notifications", payload());
      toast.success(`Sent to ${r.data.recipients} recipient(s)`);
      setForm(emptyForm); setPreview(null); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function removeBatch(id) {
    if (!window.confirm("Delete this notification for all recipients?")) return;
    try { await api.delete(`/notifications/batch/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <Button data-testid="notif-compose" className="rounded-full bg-[#0F1E4F] text-white" onClick={() => { setForm(emptyForm); setPreview(null); setOpen(true); }}>
          <Send className="h-4 w-4 mr-1.5" /> Send notification
        </Button>
      </div>

      <Card className="rounded-2xl border-slate-200 overflow-hidden">
        {sent.length === 0 ? <div className="p-10 text-center text-slate-500 text-sm">No notifications sent yet.</div> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Title</TableHead><TableHead>Target</TableHead><TableHead>Sent</TableHead>
              <TableHead>Recipients</TableHead><TableHead>Read</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {sent.map((b) => (
                <TableRow key={b.batch_id} data-testid={`notif-sent-${b.batch_id}`}>
                  <TableCell className="font-semibold text-[#0F1E4F]">{b.title}</TableCell>
                  <TableCell className="capitalize">
                    {AUDIENCES.find((a) => a.v === b.audience)?.l || b.audience}
                    {b.class_id && ` · ${classes.find((c) => c.id === b.class_id)?.name || "class"}`}
                    {b.course_id && ` · ${courses.find((c) => c.id === b.course_id)?.name || "course"}`}
                    {b.branch_id && ` · ${branches.find((c) => c.id === b.branch_id)?.name || "branch"}`}
                  </TableCell>
                  <TableCell>{new Date(b.created_at).toLocaleString()}</TableCell>
                  <TableCell>{b.recipients}</TableCell>
                  <TableCell>{b.read_count}/{b.recipients}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" data-testid={`notif-del-${b.batch_id}`} className="h-8 rounded-full border-red-200 text-red-700" onClick={() => removeBatch(b.batch_id)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-w-lg">
          <DialogHeader><DialogTitle>Send notification</DialogTitle></DialogHeader>
          <div className="grid gap-3 max-h-[65vh] overflow-auto pr-1">
            <Input data-testid="notif-title" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="h-11 rounded-xl" />
            <Textarea data-testid="notif-body" rows={4} placeholder="Message" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="rounded-xl" />
            <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}>
              <SelectTrigger data-testid="notif-audience" className="h-11 rounded-xl"><SelectValue placeholder="Audience" /></SelectTrigger>
              <SelectContent>{AUDIENCES.map((a) => <SelectItem key={a.v} value={a.v}>{a.l}</SelectItem>)}</SelectContent>
            </Select>
            <div className="grid grid-cols-3 gap-2">
              <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                <SelectTrigger data-testid="notif-branch" className="h-11 rounded-xl"><SelectValue placeholder="Branch" /></SelectTrigger>
                <SelectContent><SelectItem value={NONE}>Any branch</SelectItem>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={form.course_id} onValueChange={(v) => setForm({ ...form, course_id: v })}>
                <SelectTrigger data-testid="notif-course" className="h-11 rounded-xl"><SelectValue placeholder="Course" /></SelectTrigger>
                <SelectContent><SelectItem value={NONE}>Any course</SelectItem>{courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={form.class_id} onValueChange={(v) => setForm({ ...form, class_id: v })}>
                <SelectTrigger data-testid="notif-class" className="h-11 rounded-xl"><SelectValue placeholder="Class" /></SelectTrigger>
                <SelectContent><SelectItem value={NONE}>Any class</SelectItem>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Or pick specific people</Label>
              <div className="mt-1 max-h-40 overflow-auto border border-slate-200 rounded-xl p-2 space-y-1">
                {users.map((u) => (
                  <label key={u.id} className="flex items-center gap-2 text-sm rounded-lg px-2 py-1 hover:bg-slate-50">
                    <input type="checkbox" data-testid={`notif-user-${u.id}`} checked={form.user_ids.includes(u.id)}
                      onChange={(e) => setForm({ ...form, user_ids: e.target.checked ? [...form.user_ids, u.id] : form.user_ids.filter((x) => x !== u.id) })} />
                    <span>{u.name} <span className="text-slate-400">({u.role})</span></span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" data-testid="notif-preview" className="rounded-full" onClick={checkRecipients}>Check recipients</Button>
              {preview && <span data-testid="notif-preview-count" className="text-sm text-slate-600">{preview.count} recipient(s)</span>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="notif-send" className="rounded-full bg-[#0F1E4F] text-white" onClick={send}>Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Any role: notification inbox ---------------- */
export function NotificationsInboxTab() {
  const [list, setList] = useState([]);
  const load = () => api.get("/notifications").then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  const unread = list.filter((n) => !n.read).length;

  async function toggleRead(n) {
    try { await api.post(`/notifications/${n.id}/${n.read ? "unread" : "read"}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }
  async function readAll() {
    try { await api.post("/notifications/read-all"); load(); } catch { /* ignore */ }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Bell className="h-4 w-4 text-[#B8860B]" />
          <span data-testid="notif-unread-count">{unread} unread</span>
        </div>
        {unread > 0 && (
          <Button data-testid="notif-read-all" variant="outline" className="rounded-full" onClick={readAll}>
            <CheckCheck className="h-4 w-4 mr-1.5" /> Mark all read
          </Button>
        )}
      </div>
      <div className="space-y-3">
        {list.length === 0 && <Card className="p-8 rounded-2xl border-dashed border-slate-300 text-slate-500 text-sm text-center">No notifications yet.</Card>}
        {list.map((n) => (
          <Card key={n.id} data-testid={`notif-${n.id}`} className={`p-5 rounded-2xl ${n.read ? "border-slate-200" : "border-[#0F1E4F] bg-[#0F1E4F]/[0.03]"}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-widest text-[#B8860B]">{new Date(n.created_at).toLocaleString()} · {n.sender_name || "Academy"}</div>
                <div className="mt-1 font-serif font-black text-[#0F1E4F]">{n.title}</div>
                {n.body && <p className="mt-2 text-sm text-slate-600 whitespace-pre-line">{n.body}</p>}
              </div>
              <button data-testid={`notif-toggle-${n.id}`} onClick={() => toggleRead(n)}>
                <Badge className={`rounded-full border-0 ${n.read ? "bg-slate-100 text-slate-600" : "bg-[#0F1E4F] text-white"}`}>{n.read ? "Read" : "Unread"}</Badge>
              </button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Any role: targeted announcements feed ---------------- */
export function AnnouncementsFeedTab() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/announcements/mine").then((r) => setList(r.data)).catch(() => setList([])); }, []);
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {list.length === 0 && <Card className="p-8 rounded-2xl border-dashed border-slate-300 text-slate-500 text-sm text-center col-span-full">No announcements for you right now.</Card>}
      {list.map((a) => (
        <Card key={a.id} data-testid={`feed-ann-${a.id}`} className="p-6 rounded-2xl border-slate-200">
          {a.image_url && <img src={a.image_url} alt={a.title} className="mb-3 w-full h-40 object-cover rounded-xl" />}
          <div className="text-xs uppercase tracking-widest text-[#B8860B]">{a.date}</div>
          <div className="mt-1 font-serif font-black text-lg text-[#0F1E4F]">{a.title}</div>
          <p className="mt-2 text-sm text-slate-600 whitespace-pre-line">{a.description}</p>
        </Card>
      ))}
    </div>
  );
}
